import { useCallback } from "react"
import { t } from "i18next"
import { useApproveConsultationReportMutation } from "@/modules/patients/detail/consultation-report/services/useApproveConsultationReportMutation"
import { useDeSignGeneralAttestationMutation } from "@/modules/patients/detail/general-attestation/de/services/useDeSignGeneralAttestationMutation"
import { useChSignLaboratoryPrescriptionMutation } from "@/modules/patients/detail/laboratory-prescription/ch/services/useChSignLaboratoryPrescriptionMutation"
import { useDeSignLegalGuardianMutation } from "@/modules/patients/detail/legal-guardian-attestation/de/services/useDeSignLegalGuardianMutation"
import { useChSignMeasLaboratoryPrescriptionMutation } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/useChSignMeasLaboratoryPrescriptionMutation"
import { useChSignRadiologyPrescriptionMutation } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/useChSignRadiologyPrescriptionMutation"
import { useChSignNeuropsychologicDiagnosticPrescriptionMutation } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/services/useChSignNeuropsychologicDiagnosticPrescriptionMutation"
import { useDeSignPrescriptionMutation } from "@/modules/patients/detail/prescription/de/services/useDeSignPrescriptionMutation"
import { useDeSignTherapyPrescriptionMutation } from "@/modules/patients/detail/prescription/de/services/useDeSignTherapyPrescriptionMutation"
import { ProgressNoteDocument } from "@/modules/patients/detail/progress-note/types/document"
import { useChApproveReferralDocumentMutation } from "@/modules/patients/detail/referral/ch/services/useChApproveReferralDocumentMutation"
import { useDeSignReferralDocumentMutation } from "@/modules/patients/detail/referral/de/services/useDeSignReferralDocumentMutation"
import { useChSignChNutritionalCounsellingMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChSignChNutritionalCounsellingMutation"
import { useChSignGeneralAttestationMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChSignGeneralAttestationMutation"
import { useChSignLegalGuardianMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChSignLegalGuardianMutation"
import { useChSignPhysiotherapyPrescriptionMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChSignPhysiotherapyPrescriptionMutation"
import { useChSignPrescriptionMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChSignPrescriptionMutation"
import { useChSignPsychologicalPsychotherapyMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChSignPsychologicalPsychotherapyMutation"
import { useChSignSickLeaveMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChSignSickLeaveMutation"
import { useDeSignSickLeaveMutation } from "@/modules/patients/detail/therapy-prescription/de/services/useDeSignSickLeaveMutation"
import { useApproveAndGenerateCarePlanDocumentMutation } from "@/modules/patients/services/useApproveAndGenerateCarePlanDocument"
import { CH_DOCUMENTS, DE_DOCUMENTS, ProgressNoteDocumentType } from "@/modules/patients/types/ProgressNoteDocument"
import { Country } from "@/types/__generated__/union-types"

type QESCallback = {
  url: string
  requestId: string
}

type DocumentSigning = {
  variables: {
    id: string
    type: ProgressNoteDocumentType
    progressNoteId: string
  }
  onStart?: () => void
  onSuccess?: (document: ProgressNoteDocument) => void
  onError?: (error: Error) => void
  onQESCallback?: (options: QESCallback) => void
}

type useDocumentSigningProps = {
  countryCode: Country
  keepOriginalAuthor?: boolean
}

const DEFAULT_SIGNING_MUTATION_PARAMS = {
  confirmProps: {
    title: t("patient.conclude.unableToSignDocument"),
  },
}

const DEFAULT_APPROVAL_MUTATION_PARAMS = {
  confirmProps: {
    title: t("patient.conclude.unableToApproveDocument"),
  },
}

const useDocumentSigning = ({ countryCode, keepOriginalAuthor = false }: useDocumentSigningProps) => {
  const [signChPhysiotherapyPrescription] = useChSignPhysiotherapyPrescriptionMutation()
  const [signChPsychologicalPsychotherapy] = useChSignPsychologicalPsychotherapyMutation()
  const [signChPrescription] = useChSignPrescriptionMutation()
  const [signChSickLeave] = useChSignSickLeaveMutation()
  const [signChNutritionalCounselling] = useChSignChNutritionalCounsellingMutation()
  const [signChLegalGuardian] = useChSignLegalGuardianMutation()
  const [signChGeneralAttestation] = useChSignGeneralAttestationMutation()
  const [signChRadiologyPrescription] = useChSignRadiologyPrescriptionMutation()
  const [signChMeasLaboratoryPrescription] = useChSignMeasLaboratoryPrescriptionMutation()
  const [signChNeuropsychologicDiagnosticPrescription] = useChSignNeuropsychologicDiagnosticPrescriptionMutation()
  const [signChLaboratoryPrescription] = useChSignLaboratoryPrescriptionMutation()
  const approveAndGenerateCarePlanDocumentMutation = useApproveAndGenerateCarePlanDocumentMutation()
  const [approveReferralDocument] = useChApproveReferralDocumentMutation()
  const [approveConsultationReport] = useApproveConsultationReportMutation()

  const [signDeSickLeave] = useDeSignSickLeaveMutation()
  const [signDeGeneralAttestation] = useDeSignGeneralAttestationMutation()
  const [signDeLegalGuardian] = useDeSignLegalGuardianMutation()
  const [signDeReferralDocument] = useDeSignReferralDocumentMutation()
  const [signDePrescription] = useDeSignPrescriptionMutation()
  const [signDeTherapyPrescription] = useDeSignTherapyPrescriptionMutation()

  const signDocument = useCallback(
    async ({
      variables: { id, type, progressNoteId },
      onStart,
      onSuccess,
      onError,
      onQESCallback,
    }: DocumentSigning) => {
      onStart?.()

      if (countryCode === "CH") {
        // In general we should be exhaustive but enabling it here would bring LOT of additional types, maybe something should be revisited later...
        // oxlint-disable-next-line typescript/switch-exhaustiveness-check
        switch (type) {
          case CH_DOCUMENTS.PHYSIOTHERAPY:
            signChPhysiotherapyPrescription({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signPhysiotherapyPrescriptionDocument.ch_PhysiotherapyPrescription || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.PSYCHOLOGICAL_PSYCHOTHERAPY:
            signChPsychologicalPsychotherapy({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signPsychotherapyPrescriptionDocument.ch_PsychotherapyPrescription || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.PRESCRIPTION:
            signChPrescription({
              id,
              progressNoteId,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } = response.ch_signPrescriptionDocument.ch_Prescription || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.SICK_LEAVE:
            signChSickLeave({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } = response.ch_signSickLeaveDocument.ch_SickLeave || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.NUTRITIONAL_PRESCRIPTION:
            signChNutritionalCounselling({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signNutritionalPrescriptionDocument.ch_NutritionalPrescription || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.GUARDIAN_ATTESTATION:
            signChLegalGuardian({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signAttestationGuardianDocument.ch_AttestationGuardian || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.GENERAL_ATTESTATION:
            signChGeneralAttestation({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signGeneralAttestationDocument.ch_GeneralAttestation || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.MEAS_LABORATORY_PRESCRIPTION:
            signChMeasLaboratoryPrescription({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signMeasLabPrescriptionDocument.ch_MeasLaboratoryPrescription || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.CARE_PLAN:
            approveAndGenerateCarePlanDocumentMutation({ progressNoteId, keepOriginalAuthor }, (response) => {
              const { id, signedAt, signedBy, fileId } =
                response.approveAndGenerateCarePlanDocument.carePlanSummary || {}
              if (id) {
                onSuccess?.({
                  id,
                  progressNoteId,
                  type,
                  signedAt,
                  signedBy,
                  fileId,
                })
              }
            })
            break
          case CH_DOCUMENTS.REFERRAL_LETTER:
            approveReferralDocument({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } = response.ch_approveReferralLetter.ch_ReferralLetter || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.CONSULTATION_REPORT:
            approveConsultationReport({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_approveConsultationReport.ch_ConsultationReport || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break

          case CH_DOCUMENTS.MEAS_RADIOLOGY_PRESCRIPTION:
            signChRadiologyPrescription({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signMeasRadiologyPrescriptionDocument.ch_MeasRadiologyPrescription || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.NEUROPSYCHOLOGIC_DIAGNOSTIC_PRESCRIPTION:
            signChNeuropsychologicDiagnosticPrescription({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signNeuropsychologicDiagnosticPrescriptionDocument
                    .ch_NeuropsychologicDiagnosticPrescription || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case CH_DOCUMENTS.LABORATORY_PRESCRIPTION:
            signChLaboratoryPrescription({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.ch_signLaboratoryPrescriptionDocument.ch_LaboratoryPrescription || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          default:
            break
        }
      } else if (countryCode === "DE") {
        // In general we should be exhaustive but enabling it here would bring LOT of additional types, maybe something should be revisited later...
        // oxlint-disable-next-line typescript/switch-exhaustiveness-check
        switch (type) {
          case DE_DOCUMENTS.PRESCRIPTION:
            signDePrescription({
              id,
              onCompleted({ de_signPrescriptionDocument: { qesSigningResponse } }) {
                if (!qesSigningResponse) return

                onQESCallback?.({
                  url: qesSigningResponse.signingRequestUrl,
                  requestId: qesSigningResponse.requestId,
                })
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case DE_DOCUMENTS.THERAPY_PRESCRIPTION:
            signDeTherapyPrescription({
              id,
              onCompleted({ de_signTherapyPrescriptionDocument: { qesSigningResponse } }) {
                if (!qesSigningResponse) return

                onQESCallback?.({
                  url: qesSigningResponse.signingRequestUrl,
                  requestId: qesSigningResponse.requestId,
                })
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case DE_DOCUMENTS.SICK_LEAVE:
            signDeSickLeave({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } = response.de_signSickLeaveDocument.de_SickLeave || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case DE_DOCUMENTS.GUARDIAN_ATTESTATION:
            signDeLegalGuardian({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.de_signAttestationGuardianDocument.de_AttestationGuardian || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case DE_DOCUMENTS.GENERAL_ATTESTATION:
            signDeGeneralAttestation({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.de_signGeneralAttestationDocument.de_GeneralAttestation || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          case DE_DOCUMENTS.CARE_PLAN:
            approveAndGenerateCarePlanDocumentMutation({ progressNoteId, keepOriginalAuthor }, (response) => {
              const { id, signedAt, signedBy, fileId } =
                response.approveAndGenerateCarePlanDocument.carePlanSummary || {}
              if (id) {
                onSuccess?.({
                  id,
                  progressNoteId,
                  type,
                  signedAt,
                  signedBy,
                  fileId,
                })
              }
            })
            break
          case DE_DOCUMENTS.REFERRAL_LETTER:
            signDeReferralDocument({
              id,
              onCompleted(response) {
                const { id, signedAt, signedBy, fileId } =
                  response.de_signReferralLetterDocument.de_ReferralLetter || {}
                if (id) {
                  onSuccess?.({
                    id,
                    progressNoteId,
                    type,
                    signedAt,
                    signedBy,
                    fileId,
                  })
                }
              },
              onError(error) {
                onError?.(error)
              },
            })
            break
          default:
            break
        }
      }
    },
    [
      countryCode,
      keepOriginalAuthor,
      signChPhysiotherapyPrescription,
      signChPsychologicalPsychotherapy,
      signChPrescription,
      signChSickLeave,
      signChNutritionalCounselling,
      signChLegalGuardian,
      signChGeneralAttestation,
      signChRadiologyPrescription,
      signChMeasLaboratoryPrescription,
      signChNeuropsychologicDiagnosticPrescription,
      signChLaboratoryPrescription,
      approveAndGenerateCarePlanDocumentMutation,
      approveReferralDocument,
      approveConsultationReport,
      signDeSickLeave,
      signDeGeneralAttestation,
      signDeLegalGuardian,
      signDeReferralDocument,
      signDePrescription,
      signDeTherapyPrescription,
    ],
  )

  return signDocument
}

export { useDocumentSigning, DEFAULT_SIGNING_MUTATION_PARAMS, DEFAULT_APPROVAL_MUTATION_PARAMS }
